import React, { useEffect, useRef, useState, useContext } from "react";
import SocketContext from "../../context/SocketContext";
import useGameBase from "../../hooks/useGameBase";

// Minimal Charades client using WebRTC for media streaming
export default function CharadesGame({
  roomId,
  isHost,
  gameState,
  gamePlayers = [],
  user = {},
  onGameAction,
}) {
  const socket = useContext(SocketContext);
  const { submitAction } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
  });
  // compute whether this client is the drawer from game state + socket id
  const drawerId = gameState && gameState.drawer;
  const isDrawer = !!(
    socket &&
    socket.id &&
    drawerId &&
    socket.id === drawerId
  );
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({}); // map of socketId -> video element ref
  const pcsRef = useRef({}); // map of socketId -> RTCPeerConnection
  const [localStream, setLocalStream] = useState(null);
  // keep a ref to the actual MediaStream so effects won't restart when state is set
  const streamRef = useRef(null);
  // UI states
  const [preparing, setPreparing] = useState(false); // viewer preparing
  const [waitingPrepared, setWaitingPrepared] = useState({
    prepared: 0,
    total: 0,
    waiting: false,
  }); // drawer waiting state
  const viewersListRef = useRef([]);
  // compute drawer's display name when available
  const drawerName =
    gameState && gameState.drawer && Array.isArray(gameState.players)
      ? (gameState.players.find((p) => p.socketId === gameState.drawer) || {})
          .username || "the drawer"
      : "the drawer";
  // Guess UI state (for non-drawers)
  const [guessText, setGuessText] = useState("");
  const [submittingGuess, setSubmittingGuess] = useState(false);

  useEffect(() => {
    // Only drawer publishes media
    if (isDrawer) {
      let mounted = true;
      const start = async () => {
        try {
          const s = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          if (!mounted) {
            // If unmounted between permission prompt and now, stop tracks
            try {
              s.getTracks().forEach((t) => t.stop());
            } catch (e) {}
            return;
          }
          streamRef.current = s;
          setLocalStream(s);
          if (localVideoRef.current) localVideoRef.current.srcObject = s;
        } catch (e) {
          console.error("getUserMedia failed", e);
        }
      };
      start();
      return () => {
        mounted = false;
        // cleanup the stream we created (use ref to avoid retriggering effect)
        if (streamRef.current) {
          try {
            streamRef.current.getTracks().forEach((t) => t.stop());
          } catch (e) {}
          streamRef.current = null;
        }
        setLocalStream(null);
      };
    }
    // when not drawer, ensure we clear any existing local stream
    return () => {
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch (e) {}
        streamRef.current = null;
      }
      setLocalStream(null);
    };
  }, [isDrawer]);

  // Drawer: create peer connections to each viewer and send offer
  const createOfferFor = React.useCallback(
    async (viewerSocketId) => {
      const s = streamRef.current || localStream;
      if (!s) return;
      if (pcsRef.current[viewerSocketId]) return; // already created
      const pc = new RTCPeerConnection();
      pcsRef.current[viewerSocketId] = pc;
      // add tracks
      for (const t of s.getTracks()) pc.addTrack(t, s);
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          socket.emit("charades:webrtc:candidate", {
            roomId,
            toSocketId: viewerSocketId,
            candidate: e.candidate,
          });
        }
      };
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit("charades:webrtc:offer", {
          roomId,
          toSocketId: viewerSocketId,
          sdp: pc.localDescription,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [localStream, socket, roomId]
  );

  useEffect(() => {
    if (!socket) return;

    const handleOffer = async ({ fromSocketId, sdp, roomId: r }) => {
      if (r !== roomId) return;
      // Only viewers should handle offers (drawer will create offers to viewers)
      if (isDrawer) return;
      try {
        // Reuse prepared pc if present (prepare handler may have created it)
        let pc = pcsRef.current[fromSocketId];
        let created = false;
        if (!pc) {
          pc = new RTCPeerConnection();
          pcsRef.current[fromSocketId] = pc;
          created = true;
        }
        // Attach handlers only if we just created it (avoid duplicating handlers)
        if (created) {
          pc.ontrack = (ev) => {
            let video = remoteVideosRef.current[fromSocketId];
            if (!video) {
              const v = document.createElement("video");
              v.autoplay = true;
              v.playsInline = true;
              // mute by default so mobile browsers will autoplay
              v.muted = true;
              v.className = "w-80 h-60";
              // create a small overlay that prompts user to tap to unmute
              const wrapper = document.createElement("div");
              wrapper.className = "relative inline-block";
              wrapper.style.position = "relative";
              wrapper.style.display = "inline-block";
              const overlay = document.createElement("div");
              overlay.innerText = "Tap to unmute";
              overlay.className = "text-xs text-white bg-black bg-opacity-50 rounded px-2 py-1";
              overlay.style.position = "absolute";
              overlay.style.left = "8px";
              overlay.style.bottom = "8px";
              overlay.style.pointerEvents = "none";
              // clicking the video toggles mute and hides the overlay when unmuted
              v.addEventListener("click", () => {
                try {
                  if (v.muted) {
                    v.muted = false;
                    overlay.style.display = "none";
                  } else {
                    v.muted = true;
                    overlay.style.display = "";
                  }
                } catch (e) {}
              });
              wrapper.appendChild(v);
              wrapper.appendChild(overlay);
              remoteVideosRef.current[fromSocketId] = v;
              document.getElementById("charades-remote-videos")?.appendChild(wrapper);
              video = v;
            }
            video.srcObject = ev.streams[0];
          };
          pc.onicecandidate = (e) => {
            if (e.candidate) {
              socket.emit("charades:webrtc:candidate", {
                roomId,
                toSocketId: fromSocketId,
                candidate: e.candidate,
              });
            }
          };
        }
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("charades:webrtc:answer", {
          roomId,
          toSocketId: fromSocketId,
          sdp: pc.localDescription,
        });
      } catch (e) {
        console.error(e);
      }
    };

    const handleAnswer = async ({ fromSocketId, sdp, roomId: r }) => {
      if (r !== roomId) return;
      const pc = pcsRef.current[fromSocketId];
      if (!pc) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      } catch (e) {
        console.error(e);
      }
    };

    const handleCandidate = async ({ fromSocketId, candidate, roomId: r }) => {
      if (r !== roomId) return;
      const pc = pcsRef.current[fromSocketId];
      if (!pc) return;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.error(e);
      }
    };

    // prepare viewers when server announces a round start
    const handlePrepare = ({ roomId: r, drawer }) => {
      if (r !== roomId) return;
      // Only viewers handle prepare
      if (isDrawer) return;
      try {
        if (pcsRef.current && pcsRef.current[drawer]) return; // already prepared for this drawer
        const pc = new RTCPeerConnection();
        pcsRef.current[drawer] = pc;
        pc.ontrack = (ev) => {
          let video = remoteVideosRef.current[drawer];
            if (!video) {
              const v = document.createElement("video");
              v.autoplay = true;
              v.playsInline = true;
              // mute by default for mobile autoplay
              v.muted = true;
              v.className = "w-80 h-60";
              const wrapper = document.createElement("div");
              wrapper.className = "relative inline-block";
              wrapper.style.position = "relative";
              wrapper.style.display = "inline-block";
              const overlay = document.createElement("div");
              overlay.innerText = "Tap to unmute";
              overlay.className = "text-xs text-white bg-black bg-opacity-50 rounded px-2 py-1";
              overlay.style.position = "absolute";
              overlay.style.left = "8px";
              overlay.style.bottom = "8px";
              overlay.style.pointerEvents = "none";
              v.addEventListener("click", () => {
                try {
                  if (v.muted) {
                    v.muted = false;
                    overlay.style.display = "none";
                  } else {
                    v.muted = true;
                    overlay.style.display = "";
                  }
                } catch (e) {}
              });
              wrapper.appendChild(v);
              wrapper.appendChild(overlay);
              remoteVideosRef.current[drawer] = v;
              document.getElementById("charades-remote-videos")?.appendChild(wrapper);
              video = v;
            }
          video.srcObject = ev.streams[0];
          // once we actually receive track from drawer, clear preparing state
          try {
            setPreparing(false);
          } catch (e) {}
        };
        pc.onicecandidate = (e) => {
          if (e.candidate)
            socket.emit("charades:webrtc:candidate", {
              roomId,
              toSocketId: drawer,
              candidate: e.candidate,
            });
        };
        // Notify server that we have prepared our PC
        try {
          socket.emit("charades:prepared", { roomId });
        } catch (e) {
          /* ignore */
        }
        try {
          setPreparing(true);
        } catch (e) {}
      } catch (e) {
        console.error("prepare handler failed", e);
      }
    };

    socket.on("charades:webrtc:offer", handleOffer);
    socket.on("charades:webrtc:answer", handleAnswer);
    socket.on("charades:webrtc:candidate", handleCandidate);
    socket.on("charades:prepare", handlePrepare);
    // Instead of auto-offering on viewersList, wait for the server to tell us all viewers are ready
    const handleViewersListUI = ({ roomId: r, viewers }) => {
      if (r !== roomId) return;
      if (!isDrawer) return;
      viewersListRef.current = Array.isArray(viewers) ? viewers : [];
      setWaitingPrepared({
        prepared: 0,
        total: viewersListRef.current.length,
        waiting: true,
      });
    };

    const handleViewersReady = async ({ roomId: r, viewers }) => {
      if (r !== roomId) return;
      if (!isDrawer) return;
      // mark all prepared
      try {
        setWaitingPrepared({
          prepared: viewers ? viewers.length : 0,
          total: viewers ? viewers.length : 0,
          waiting: false,
        });
      } catch (e) {}
      // Wait until localStream is ready (may be async due to getUserMedia permission)
      const waitForStream = async (timeout = 5000) => {
        const start = Date.now();
        while (!localStream && Date.now() - start < timeout) {
          await new Promise((res) => setTimeout(res, 100));
        }
        return !!localStream;
      };
      try {
        const ready = await waitForStream(10000);
        if (!ready) {
          console.warn("Charades: localStream not ready for auto-offer");
          return;
        }
        if (Array.isArray(viewers)) {
          for (const v of viewers) {
            try {
              await createOfferFor(v);
            } catch (e) {
              console.error("createOfferFor failed", e);
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
    };

    const handleViewersPreparedPartial = ({ roomId: r, total, prepared }) => {
      if (r !== roomId) return;
      if (!isDrawer) return;
      try {
        setWaitingPrepared({
          prepared: prepared || 0,
          total: total || 0,
          waiting: true,
        });
      } catch (e) {}
    };

    socket.on("charades:viewersReady", handleViewersReady);
    socket.on("charades:viewersPreparedPartial", handleViewersPreparedPartial);
    socket.on("charades:viewersList", handleViewersListUI);

    return () => {
      socket.off("charades:webrtc:offer", handleOffer);
      socket.off("charades:webrtc:answer", handleAnswer);
      socket.off("charades:webrtc:candidate", handleCandidate);
      socket.off("charades:viewersReady", handleViewersReady);
      socket.off(
        "charades:viewersPreparedPartial",
        handleViewersPreparedPartial
      );
      socket.off("charades:viewersList", handleViewersListUI);
      socket.off("charades:prepare", handlePrepare);
    };
  }, [socket, roomId, createOfferFor, localStream, isDrawer, drawerId]);

  // expose for quick manual testing from console: window.createOfferFor('<socketId>')
  useEffect(() => {
    window.createOfferFor = createOfferFor;
    return () => {
      try {
        delete window.createOfferFor;
      } catch (e) {}
    };
  }, [createOfferFor]);

  // Cleanup peer connections if phase changes away from 'draw' (round ended)
  useEffect(() => {
    try {
      const phase = gameState && gameState.phase;
      if (phase && phase !== "draw") {
        // close all peer connections and remove remote videos
        for (const [id, pc] of Object.entries(pcsRef.current || {})) {
          try {
            pc.close();
          } catch (e) {}
          delete pcsRef.current[id];
        }
        const container = document.getElementById("charades-remote-videos");
        if (container) {
          while (container.firstChild)
            container.removeChild(container.firstChild);
        }
        remoteVideosRef.current = {};
      }
    } catch (e) {
      console.error(e);
    }
  }, [gameState]);

  // Expose a small UI for demo: local preview + remote container
  return (
    <div className="charades-game">
      <div>
        {isDrawer && (
          <div>
            {gameState.phase === "choosePrompt" && (
              <div className="text-gray-600 dark:text-gray-400 mb-4">
                Your video isn't shown until you pick your word/phrase
              </div>
            )}
            {gameState.phase === "draw" && (
              <div className="text-red-600 dark:text-red-400 pulsate mb-4">
                Broadcasting!
              </div>
            )}
            {gameState.phase !== "reveal" && (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-80 h-60 bg-black mb-2"
              />
            )}
            {gameState.phase === "draw" && (
              <div>
                <div className="text-gray-600 dark:text-gray-400 mb-4">
                  You are the actor - visually describe the answer
                </div>
                {/* {waitingPrepared && waitingPrepared.waiting && (
                  <div className="mt-2 p-2 bg-amber-100 text-amber-700 rounded">
                    Waiting for viewers: {waitingPrepared.prepared} / {waitingPrepared.total}
                  </div>
                )} */}
              </div>
            )}

            {/* Show the chosen word to the drawer during the draw phase */}
            {gameState.phase === "draw" && gameState.currentWord && (
              <div className="mt-3 text-yellow-900 dark:text-yellow-100 rounded">
                <div className="">
                  You are acting "
                  <span className="font-bold">{gameState.currentWord}</span>"
                </div>
              </div>
            )}
            {gameState.phase === "draw" && <DrawTimer gameState={gameState} />}
            {gameState.phase === "reveal" && !gameState.finished && (
              <RevealTimer gameState={gameState} />
            )}

            {/* Drawer prompt choices (when server is in choosePrompt) */}
            {gameState.phase === "choosePrompt" &&
              Array.isArray(gameState.promptChoices) &&
              gameState.promptChoices.length > 0 && (
                <div className="mt-3">
                  {/* <div className="text-gray-600 dark:text-gray-400 mb-4">Video Preview (not shown to anyone yet)</div>
                    <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-80 h-60 bg-black"
            /> */}
                  <div className="mt-4 mb-2 text-gray-800 dark:text-gray-200">
                    Pick a prompt to act out
                  </div>
                  <div className="flex flex-col gap-2">
                    {gameState.promptChoices.map((c, idx) => {
                      const text =
                        c && typeof c === "object" ? c.text : String(c || "");
                      const diff =
                        c && typeof c === "object" && c.difficulty
                          ? c.difficulty
                          : null;
                      const bgClass =
                        diff === "easy"
                          ? "bg-green-600 hover:bg-green-700"
                          : diff === "medium"
                          ? "bg-orange-500 hover:bg-orange-600"
                          : diff === "hard"
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-gray-600";
                      return (
                        <button
                          key={idx}
                          onClick={async () => {
                            try {
                              const action = {
                                type: "choosePrompt",
                                index: idx,
                              };
                              if (typeof onGameAction === "function")
                                onGameAction(action);
                              else if (typeof submitAction === "function")
                                await submitAction(action);
                              else if (socket)
                                socket.emit("gameAction", { roomId, action });
                            } catch (e) {
                              console.error("choose prompt failed", e);
                            }
                          }}
                          className={`px-3 py-2 rounded text-white text-left ${bgClass}`}
                        >
                          <span className="font-semibold truncate">{text}</span>
                          {diff && (
                            <span className="text-xs bg-white text-gray-800 px-2 ml-2 rounded lowercase">
                              {diff}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    You have limited time to choose; the server will auto-pick
                    if you don't.
                  </div>
                  <div className="mt-2">
                    <PromptTimer gameState={gameState} />
                  </div>
                </div>
              )}
          </div>
        )}

        {(!isDrawer) && (
          <div>
            {gameState && gameState.phase === "draw" && (
              <>
                {/* Always render the remote videos container for all non-actors (including true spectators) */}
                <div
                  id="charades-remote-videos"
                  className="flex gap-2 flex-wrap mt-2 mb-2"
                />
                <div className="text-gray-600 dark:text-gray-400 mb-4">
                  Watch {drawerName} and guess what they're acting out!
                </div>
                <DrawTimer gameState={gameState} />
                {/* Guess input for viewers/players only (not for true spectators) */}
                {user && user.socketId && gamePlayers.some(p => p.socketId === user.socketId) && (
                  <div className="mt-3">
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const text = (guessText || "").trim();
                        if (!text) return;
                        setSubmittingGuess(true);
                        try {
                          const action = { type: "guess", guess: text };
                          if (typeof onGameAction === "function") {
                            onGameAction(action);
                          } else if (typeof submitAction === "function") {
                            await submitAction(action);
                          } else if (socket) {
                            socket.emit("gameAction", { roomId, action });
                          }
                          setGuessText("");
                        } catch (err) {
                          console.error("Failed to submit guess", err);
                        } finally {
                          setSubmittingGuess(false);
                        }
                      }}
                    >
                      <label className="block mb-2 text-gray-800 dark:text-gray-200">
                        Make a guess:
                      </label>
                      <div className="flex gap-2">
                        <input
                          value={guessText}
                          onChange={(e) => setGuessText(e.target.value)}
                          disabled={submittingGuess}
                          placeholder="Type your guess and press Enter"
                          className="border px-2 py-1 rounded flex-1"
                        />
                        <button
                          type="submit"
                          className="px-3 py-1 bg-green-500 text-white rounded"
                          disabled={submittingGuess || !guessText.trim()}
                        >
                          Guess
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </>
            )}
            {gameState && gameState.phase === "choosePrompt" && (
              <div className="mt-2 mb-4 p-2 rounded text-sm text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-700">
                {drawerName} is choosing a prompt…{" "}
                <PromptTimer gameState={gameState} />
              </div>
            )}
            {preparing && (
              <div className="mt-2 p-2 bg-gray-200 text-gray-700 rounded">
                Preparing to receive stream…
              </div>
            )}
            {gameState && gameState.phase === "draw" && (
              <></>
            )}
            {gameState &&
              gameState.phase === "reveal" &&
              !gameState.finished && <RevealTimer gameState={gameState} />}
          </div>
        )}
      </div>
      {/* Recent guesses list from gameState.guesses */}
      {gameState.phase === "draw" && (
       <div className="mt-2 text-gray-800 dark:text-gray-200">
        <strong>Recent guesses:</strong>
        <div className="mt-2">
            {Object.keys(gameState && gameState.guesses ? gameState.guesses : {}).length > 0 ? (
              Object.entries(gameState.guesses || {}).map(([sid, arr]) => {
                // arr may be an array of guess objects or a single string (legacy)
                const last = Array.isArray(arr) ? arr.slice(-1)[0] : (typeof arr === 'string' ? { guess: arr, correct: false } : { guess: String(arr || ''), correct: false });
                const player = (gamePlayers || []).find(p => p.socketId === sid) || { username: sid };
                const displayName = player.username || player.name || sid;
                return (
                  <div key={sid} className={`px-2 py-1 rounded mb-1 ${last && last.correct ? 'bg-green-100 text-green-800' : 'bg-gray-100 dark:bg-gray-800 text-gray-800'}`}>
                    <span className="font-semibold mr-2">{displayName}:</span>
                    {last && last.correct ? (
                      <span className="italic">guessed the word correctly</span>
                    ) : (
                      <span>{last ? last.guess : ''}</span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-gray-600 dark:text-gray-400">No guesses yet</div>
            )}
        </div>
      </div>
      )}
    </div>
  );
}

// DrawTimer component (same implementation as CanvasCraze)
function DrawTimer({ gameState }) {
  const [secsLeft, setSecsLeft] = useState(null);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const endsAt =
      gameState.roundEndsAt ||
      (gameState.startTime
        ? gameState.startTime + (gameState.timeLimit || 60) * 1000
        : null);
    const timeLimit = gameState.timeLimit || 60;
    if (!endsAt) {
      setSecsLeft(null);
      setPct(0);
      return;
    }
    const start = endsAt - timeLimit * 1000;
    const update = () => {
      const now = Date.now();
      const msLeft = Math.max(0, endsAt - now);
      const elapsed = Math.max(0, now - start);
      const p = Math.min(1, elapsed / (timeLimit * 1000));
      setSecsLeft(Math.ceil(msLeft / 1000));
      setPct(p);
    };
    update();
    const id = setInterval(update, 250);
    return () => clearInterval(id);
  }, [gameState.roundEndsAt, gameState.startTime, gameState.timeLimit]);

  if (secsLeft === null) return null;

  return (
    <>
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden mt-3">
        <div
          className="h-full bg-blue-400 dark:bg-yellow-600"
          style={{
            width: `${Math.min(100, pct * 100)}%`,
            transition: "width 300ms linear",
          }}
        />
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">
        {secsLeft}s left
      </div>
    </>
  );
}

// RevealTimer component (same as CanvasCraze)
function RevealTimer({ gameState }) {
  const [secsLeft, setSecsLeft] = useState(null);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const endsAt = gameState.revealEndsAt || null;
    if (!endsAt) {
      setSecsLeft(null);
      setPct(0);
      return;
    }
    const duration = Math.max(0, endsAt - Date.now());
    const start = endsAt - duration;
    const update = () => {
      const now = Date.now();
      const msLeft = Math.max(0, endsAt - now);
      const elapsed = Math.max(0, now - start);
      const p = duration > 0 ? Math.min(1, elapsed / duration) : 1;
      setSecsLeft(Math.ceil(msLeft / 1000));
      setPct(p);
    };
    update();
    const id = setInterval(update, 250);
    return () => clearInterval(id);
  }, [gameState.revealEndsAt]);

  if (secsLeft === null) return null;

  return (
    <>
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden mt-3">
        <div
          className="h-full bg-indigo-400 dark:bg-indigo-600"
          style={{
            width: `${Math.min(100, pct * 100)}%`,
            transition: "width 300ms linear",
          }}
        />
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">
        Next round in {secsLeft}s
      </div>
      {gameState.currentWord && (
        <div className="mt-2 text-center text-lg font-semibold text-yellow-800 dark:text-yellow-200">
          The word was : <b>{gameState.currentWord}</b>
        </div>
      )}
    </>
  );
}

// PromptTimer: shows countdown while drawer picks a prompt (uses gameState.promptExpiresAt)
function PromptTimer({ gameState }) {
  const [secsLeft, setSecsLeft] = useState(null);
  const [pct, setPct] = useState(0);

  const expiresAt = gameState
    ? gameState.promptExpiresAt || Date.now() + 20000
    : null;
  const startAt = gameState
    ? gameState.promptStartsAt ||
      (expiresAt ? expiresAt - 20000 : Date.now() - 20000)
    : null;
  useEffect(() => {
    if (!expiresAt || !startAt) {
      setSecsLeft(null);
      setPct(0);
      return;
    }
    const duration = Math.max(1000, expiresAt - startAt);
    const update = () => {
      const now = Date.now();
      const msLeft = Math.max(0, expiresAt - now);
      const elapsed = Math.max(0, now - startAt);
      const p = duration > 0 ? Math.min(1, elapsed / duration) : 1;
      setSecsLeft(Math.ceil(msLeft / 1000));
      setPct(p);
    };
    update();
    const id = setInterval(update, 250);
    return () => clearInterval(id);
  }, [expiresAt, startAt]);

  if (secsLeft === null) return null;
  return (
    <div>
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
        <div
          className="h-full bg-blue-600 dark:bg-yellow-400"
          style={{
            width: `${Math.min(100, pct * 100)}%`,
            transition: "width 300ms linear",
          }}
        />
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-100 mt-1">
        {secsLeft}s to choose
      </div>
    </div>
  );
}

CharadesGame.Guide = function CharadesGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Charades</h3>
      <p className="mb-1">
        A fun party game where players act out a word or phrase without
        speaking.
      </p>
      <p className="mb-1">
        The objective is for teammates to guess the word or phrase based on the
        actor's gestures.
      </p>
      <p className="mb-1">
        Players take turns being the actor while others guess.
      </p>
      <p className="mb-1">
        The game ends when all words have been acted out or a time limit is
        reached.
      </p>
      <p className="mb-1">
        Charades is a game of creativity, teamwork, and quick thinking.
      </p>
      <p className="mb-1">
        Understanding the clues and thinking outside the box is key to success.
      </p>
    </div>
  );
};
